import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { SearchInput } from '@/components/ui/search-input';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import {
  ClipboardList, Plus, Play, CheckCircle2, BarChart3,
  Loader2, Package, ChevronLeft, Save, MapPin,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StockTake {
  id: string;
  reference: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
  location?: { id: string; name: string } | null;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  _count?: { items: number };
}

interface StockTakeItem {
  id: string;
  productId: string;
  variantId?: string;
  expectedQty: number;
  countedQty: number | null;
  variance: number | null;
  notes?: string;
  product: { id: string; name: string; sku: string; stockQuantity: number; imageUrl?: string };
}

// ─── Stock Take List ─────────────────────────────────────────────────────────

export function StockTakePage() {
  const { t } = useTranslation('inventory');
  const { hasPermission } = useAuthStore();
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const queryClient = useQueryClient();
  const canWrite = hasPermission('inventory:update') || hasPermission('inventory:*');

  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeStockTakeId, setActiveStockTakeId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({ notes: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['stock-takes', page],
    queryFn: () => api.get(`/stock-takes?page=${page}&limit=20`).then((r) => r.data),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (d: { notes?: string; locationId?: string }) =>
      api.post('/stock-takes', d).then((r) => r.data as StockTake),
    onSuccess: (newTake) => {
      toast.success(t('stStarted', { reference: newTake.reference }));
      queryClient.invalidateQueries({ queryKey: ['stock-takes'] });
      setCreateOpen(false);
      setActiveStockTakeId(newTake.id);
    },
    onError: (err) => notify.error(err, t('stCreateFailed')),
  });

  const stockTakes: StockTake[] = data?.data ?? [];

  if (activeStockTakeId) {
    return (
      <StockTakeCounter
        id={activeStockTakeId}
        onBack={() => {
          setActiveStockTakeId(null);
          queryClient.invalidateQueries({ queryKey: ['stock-takes'] });
        }}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('stPageTitle')}
        subtitle={t('stPageSubtitle')}
        action={canWrite && (
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('stNew')}
          </Button>
        )}
      />

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <SkeletonTable rows={5} cols={5} />
          ) : stockTakes.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title={t('stEmptyTitle')}
              subtitle={t('stEmptySubtitle')}
              action={canWrite ? { label: t('stNew'), onClick: () => setCreateOpen(true) } : undefined}
            />
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  {[t('stColReference'), t('stColLocation'), t('stColItems'), t('stColStatus'), t('stColDate'), ''].map((h, idx) => (
                    <Th key={idx}>{h}</Th>
                  ))}
                </Tr>
              </THead>
              <TBody>
                {stockTakes.map((st) => (
                  <Tr key={st.id}>
                    <Td label={t('stColReference')} className="font-mono text-sm font-medium">{st.reference}</Td>
                    <Td label={t('stColLocation')} className="text-sm">{st.location?.name ?? t('stLegacyLocation')}</Td>
                    <Td label={t('stColItems')} className="text-sm">{t('stProductsCount', { count: st._count?.items ?? 0 })}</Td>
                    <Td label={t('stColStatus')}><StatusBadge status={st.status} /></Td>
                    <Td label={t('stColDate')} className="text-sm text-muted-foreground">{formatDate(st.createdAt)}</Td>
                    <Td>
                      {st.status === 'IN_PROGRESS' && (
                        <Button variant="ghost" size="sm" onClick={() => setActiveStockTakeId(st.id)} className="gap-1.5">
                          <Play className="h-3.5 w-3.5" />
                          {t('stContinue')}
                        </Button>
                      )}
                      {st.status === 'COMPLETED' && (
                        <Button variant="ghost" size="sm" onClick={() => setActiveStockTakeId(st.id)} className="gap-1.5">
                          <BarChart3 className="h-3.5 w-3.5" />
                          {t('stViewReport')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={20} total={data?.total ?? 0} onPageChange={setPage} />
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog
        visible={createOpen}
        onHide={() => setCreateOpen(false)}
        header={<div className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />{t('stNew')}</div>}
        modal
        style={{ width: '480px' }}
        breakpoints={{ '640px': '95vw' }}
      >
        <div className="space-y-4 pt-2">
          <div>
            <Label>{t('stDialogLocation')}</Label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{selectedLocationName ?? t('stDefaultLocation')}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('stLocationHint')}
            </p>
          </div>
          <div>
            <Label>{t('stNotesLabel')}</Label>
            <Input
              className="mt-1"
              placeholder={t('stNotesPlaceholder')}
              value={createForm.notes}
              onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('stCreateDescription', { location: selectedLocationName ?? t('stDefaultLocationLower') })}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('stCancel')}</Button>
            <Button
              onClick={() =>
                createMutation.mutate({
                  notes: createForm.notes || undefined,
                  locationId: selectedLocationId ?? undefined,
                })
              }
              disabled={createMutation.isPending}
              className="gap-2"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('stStart')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

const FILTER_LABEL_KEY = {
  all: 'stFilterAll',
  uncounted: 'stFilterUncounted',
  counted: 'stFilterCounted',
} as const;

// ─── Stock Take Counter (step-by-step counting UI) ───────────────────────────

function StockTakeCounter({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation('inventory');
  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();
  const canWrite = hasPermission('inventory:update') || hasPermission('inventory:*');

  const [search, setSearch] = useState('');
  const [pendingCounts, setPendingCounts] = useState<Record<string, number | ''>>({});
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [filter, setFilter] = useState<'all' | 'counted' | 'uncounted'>('all');

  const { data: stockTake, isLoading } = useQuery<StockTake & { items: StockTakeItem[] }>({
    queryKey: ['stock-take', id],
    queryFn: () => api.get(`/stock-takes/${id}`).then((r) => r.data),
    staleTime: 10_000,
  });

  const { data: report } = useQuery({
    queryKey: ['stock-take-report', id],
    queryFn: () => api.get(`/stock-takes/${id}/variance-report`).then((r) => r.data),
    enabled: stockTake?.status === 'COMPLETED',
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: (counts: Array<{ itemId: string; countedQty: number }>) =>
      api.patch(`/stock-takes/${id}/counts`, { counts }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('stCountsSaved'));
      setPendingCounts({});
      queryClient.invalidateQueries({ queryKey: ['stock-take', id] });
    },
    onError: (err) => notify.error(err, t('stSaveFailed')),
  });

  const completeMutation = useMutation({
    mutationFn: (apply: boolean) =>
      api.post(`/stock-takes/${id}/complete?applyAdjustments=${apply}`).then((r) => r.data),
    onSuccess: (_data, apply) => {
      toast.success(apply ? t('stCompleted') : t('stCompletedNoAdjust'));
      queryClient.invalidateQueries({ queryKey: ['stock-take', id] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err) => notify.error(err, t('stCompleteFailed')),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/stock-takes/${id}/cancel`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('stCancelled'));
      onBack();
    },
    onError: (err) => notify.error(err, t('stCancelFailed')),
  });

  const handleSavePending = () => {
    const counts = Object.entries(pendingCounts)
      .filter(([, v]) => v !== '')
      .map(([itemId, countedQty]) => ({ itemId, countedQty: Number(countedQty) }));
    if (counts.length === 0) { toast.error(t('stNoCountsToSave')); return; }
    saveMutation.mutate(counts);
  };

  const items: StockTakeItem[] = stockTake?.items ?? [];

  const filteredItems = items
    .filter((i) => {
      if (filter === 'counted') return i.countedQty !== null;
      if (filter === 'uncounted') return i.countedQty === null;
      return true;
    })
    .filter((i) => !search || i.product.name.toLowerCase().includes(search.toLowerCase()) || i.product.sku.toLowerCase().includes(search.toLowerCase()));

  const countedCount = items.filter((i) => i.countedQty !== null).length;
  const uncountedCount = items.length - countedCount;
  const progress = items.length > 0 ? Math.round((countedCount / items.length) * 100) : 0;
  const isCompleted = stockTake?.status === 'COMPLETED';
  const isCancelled = stockTake?.status === 'CANCELLED';
  const isReadOnly = isCompleted || isCancelled;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ChevronLeft className="h-4 w-4" />
          {t('stBack')}
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{stockTake?.reference ?? t('stLoading')}</h1>
          <p className="text-sm text-muted-foreground">
            {stockTake?.location?.name ?? t('stLegacyLocation')} · {isLoading ? '...' : t('stProductsCount', { count: items.length })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isReadOnly && canWrite && (
            <>
              <Button variant="outline" onClick={handleSavePending} disabled={saveMutation.isPending || Object.keys(pendingCounts).length === 0} className="gap-2">
                {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t('stSaveCounts')}
              </Button>
              <Button variant="outline" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending} className="gap-2 text-destructive-text border-destructive hover:bg-destructive/10">
                {t('stCancelTake')}
              </Button>
              <Button onClick={() => setConfirmComplete(true)} disabled={completeMutation.isPending} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                {t('stComplete')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress */}
      {!isReadOnly && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{t('stProgress')}</span>
              <span className="text-sm text-muted-foreground">{t('stCountedProgress', { counted: countedCount, total: items.length, percent: progress })}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Variance report for completed takes */}
      {isCompleted && report && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: t('stTotalItems'), value: report.totalItems },
            { label: t('stMatched'), value: report.matched, className: 'text-success-text' },
            { label: t('stGains'), value: report.gains, className: 'text-info-text' },
            { label: t('stLosses'), value: report.losses, className: 'text-destructive-text' },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.className ?? ''}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Items */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          {/* Toolbar */}
          <div className="flex gap-2 flex-wrap">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('stSearchProducts')}
              className="max-w-xs"
            />
            <div className="flex gap-1 border rounded-lg p-1">
              {(['all', 'uncounted', 'counted'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${filter === f ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                >
                  {t(FILTER_LABEL_KEY[f])}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <SkeletonTable rows={8} cols={5} />
          ) : filteredItems.length === 0 ? (
            <EmptyState icon={Package} title={t('stNoProducts')} subtitle={t('stAllCounted')} />
          ) : (
            <div className="space-y-1">
              {filteredItems.map((item) => {
                const pending = pendingCounts[item.id];
                const displayCount = pending !== undefined ? pending : item.countedQty;
                const variance = displayCount !== '' && displayCount !== null && displayCount !== undefined
                  ? Number(displayCount) - item.expectedQty
                  : item.variance;

                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-4 rounded-lg border p-3 transition-colors ${
                      item.countedQty !== null && pending === undefined ? 'border-success/30 bg-success/10' : 'hover:bg-muted/40'
                    }`}
                  >
                    {/* Product info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">{item.product.sku}</p>
                    </div>

                    {/* Expected qty */}
                    <div className="text-center w-16">
                      <p className="text-xs text-muted-foreground">{t('stExpected')}</p>
                      <p className="text-sm font-semibold">{item.expectedQty}</p>
                    </div>

                    {/* Counted qty input */}
                    <div className="w-24">
                      {isReadOnly ? (
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">{t('stCounted')}</p>
                          <p className="text-sm font-semibold">{item.countedQty ?? '—'}</p>
                        </div>
                      ) : (
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="text-center h-9 text-sm"
                          placeholder={t('stCountPlaceholder')}
                          value={pending !== undefined ? pending : (item.countedQty ?? '')}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPendingCounts((p) => ({ ...p, [item.id]: val === '' ? '' : Number(val) }));
                          }}
                        />
                      )}
                    </div>

                    {/* Variance badge */}
                    {variance !== null && variance !== undefined && (
                      <div className={`w-16 text-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        variance === 0 ? 'bg-success/10 text-success-text'
                        : variance > 0 ? 'bg-info/10 text-info-text'
                        : 'bg-destructive/10 text-destructive-text'
                      }`}>
                        {variance > 0 ? `+${variance}` : variance}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complete confirmation */}
      <ConfirmDialog
        open={confirmComplete}
        title={t('stConfirmTitle')}
        message={uncountedCount > 0
          ? t('stConfirmPartial', { uncounted: uncountedCount, total: items.length })
          : t('stConfirmApply')}
        confirmLabel={uncountedCount > 0 ? t('stConfirmLabelPartial') : t('stConfirmLabel')}
        variant="warning"
        onConfirm={() => { completeMutation.mutate(uncountedCount === 0); setConfirmComplete(false); }}
        onCancel={() => setConfirmComplete(false)}
      />
    </div>
  );
}
